;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:ENCON_SENTRY_KEY":true});}catch(e){}}();import {debounce} from "lodash";
import {useLocale, useTranslations} from "next-intl";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useRouter} from "@/i18n/routing";
import Button from "../../Button";
import "./search.scss";
import Container from "@/components/Container";
import Icon from "@/components/Icon";
import {CMSLink} from "@/components/Link";

export default function HeaderSearch() {
	const t = useTranslations("Header");
	const router = useRouter();
	const locale = useLocale();
	const [search, setSearch] = useState("");
	const [suggestions, setSuggestions] = useState<any[]>([]); // Add type annotation
	const [isLoading, setIsLoading] = useState(false);
	const searchRef = useRef<HTMLDivElement>(null);
	const [visible, setVisible] = useState(false);

	const fetchSuggestions = useCallback(
		async (searchQuery: string) => {
			setIsLoading(true);
			try {
				const res = await fetch(
					`/api/site-search?q=${encodeURIComponent(searchQuery)}&locale=${locale}`,
				);
				const data = await res.json();
				setSuggestions(data.results.docs);
			} catch (error) {
				console.error("Search error:", error);
				setSuggestions([]);
			} finally {
				setIsLoading(false);
			}
		},
		[locale],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const debouncedSearch = useMemo(
		() => debounce(fetchSuggestions, 300),
		[locale],
	);

	const onSearch = (e: React.FormEvent) => {
		e.preventDefault();
	};

	const handleSearch = () => {
		setVisible(false);
		router.push(`/search?query=${search}`);
		setSearch("");
		setSuggestions([]);
	};

	const closeSearch = () => {
		setVisible(false);
		setSearch("");
		setSuggestions([]);
	};

	const handleClick = () => {
		setVisible(false);
		setSearch("");
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearch(value);

		if (value.length >= 2) {
			debouncedSearch(value);
		} else {
			setSuggestions([]);
		}
	};

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				searchRef.current &&
				!searchRef.current.contains(event.target as Node)
			) {
				setVisible(false);
				setSuggestions([]);
			}
		}
		if (visible) {
			document.addEventListener("mousedown", handleClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [visible]);

	return (
		<form className="header-search" onSubmit={onSearch}>
			<div
				id="header-search"
				ref={searchRef}
				className={visible ? "visible" : ""}
			>
				<Container>
					<div className="search-container-wrapper">
						<h2>{t("search.placeholder")}</h2>
						<div className="header-search__input-wrapper">
							<input
								name="search"
								className="header-search__input"
								type="search"
								value={search}
								onChange={handleInputChange}
							/>
							<Button
								link="#"
								variant="inline"
								className="search-button"
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										handleSearch();
										router.push(`/search?query=${search}`);
									}
								}}
								onClick={(e) => {
									e.preventDefault();
									handleSearch();
								}}
							>
								<Icon name="search" />
							</Button>
						</div>
						{search.length >= 2 && (
							<div className="search-suggestions">
								{isLoading ? (
									<div className="search-suggestions__loading">
										{t("search.loading")}
									</div>
								) : suggestions?.length > 0 ? (
									<ul className="search-suggestions__list">
										{suggestions.map((suggestion: any) => (
											<li key={suggestion.slug}>
												{suggestion.doc.relationTo === "cases" ? (
													<CMSLink
														appearance="inline"
														type="reference"
														url={`/cases/${suggestion.slug}`}
														onClick={handleClick}
														{...suggestion}
													>
														{suggestion.title}
													</CMSLink>
												) : suggestion.doc.relationTo === "insights" ? (
													<CMSLink
														appearance="inline"
														type="reference"
														url={`/insights/${suggestion.slug}`}
														onClick={handleClick}
														{...suggestion}
													>
														{suggestion.title}
													</CMSLink>
												) : (
													<CMSLink
														appearance="inline"
														type="reference"
														url={suggestion.slug}
														onClick={handleClick}
														{...suggestion}
													>
														{suggestion.title}
													</CMSLink>
												)}
											</li>
										))}
									</ul>
								) : (
									<div className="search-suggestions__empty">
										{t("search.noResults")}
									</div>
								)}
							</div>
						)}
					</div>
					<Icon onClick={closeSearch} name="close" className="search-close" />
				</Container>
			</div>
			<Button
				link="#"
				variant="secondary"
				type="submit"
				className="header-search__button"
				onClick={(e) => {
					e.preventDefault();
					setVisible(true);
				}}
			>
				<span className="sr-only">{t("search.submit")}</span>
				<Icon name="search" />
				<span>{t("search.submit")}</span>
			</Button>
		</form>
	);
}
